import React, { useState, useMemo, useEffect } from "react";
import { List, Card, Button, Space, Divider, Collapse, Popconfirm } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import "./procurement-library.scss";
import {
  ArrowRight,
  BookInfoIcon,
  BookSearchIcon,
  HandshakeIcon,
  OvalIcon,
} from "../../lib/icon";
import { Link } from "react-router-dom";
import {
  SOURCING_STRATEGY_CYCLE_TABS,
  SOURCING_STRATEGY_CYCLE_TOOLTIPS,
  TOTAL_COST_OF_OWNERSHIP_CHART_TEXT_CONTENT,
  PROCUREMENT_CATEGORIES_ID,
  CheckIconItem,
  SOURCING_STRATEGY_CYCLE_COLORS,
} from "./config";
import { api } from "../../lib";
import FooterDisclaimer from "../income-driver-calculator/components/FooterDisclaimer";
import { useWindowDimensions } from "../../hooks";
import { OtherToolsAndResources } from "../../components/utils";
import SustainableProcurementImage from "../../assets/images/procurement-library/sustainable-procurement.png";
import SourcingStrategyCycleImage from "../../assets/images/procurement-library/sourcing-strategy-cycle.png";
import ProcurementPrinciplesCurves from "../../assets/images/procurement-library/principles-for-procurement-curves.svg";
import CheckCircleIcon from "../../assets/icons/procurement-library/check-circle.png";
import TCOChartImage from "../../assets/images/procurement-library/total-cost-of-ownership-chart.png";
import { PLState } from "../../store";

const cards = [
  {
    title: "Introduction to integrating Sustainability into Procurement",
    url: "#sourcing-strategy-cycle-content",
    cta: "Explore",
    icon: <BookInfoIcon />,
  },
  {
    title: "Explore how Sustainable procurement practices fit your business",
    url: "/procurement-library/assessment",
    cta: "Explore",
    icon: <HandshakeIcon />,
  },
  {
    title: "Explore the Intervention Library",
    url: "/procurement-library/intervention-library",
    cta: "Explore",
    icon: <BookSearchIcon />,
  },
];

const SourcingStrategyCycleTabs = ({
  sourcingStrategyCycleTab,
  setSourcingStrategyCycleTab,
  remappedSourcingStrategyCycleTabs,
}) => {
  return (
    <div className="sscc-tabs-wrapper">
      {remappedSourcingStrategyCycleTabs.map((item) => (
        <div
          key={`sscc-tab-${item.key}`}
          className={`sscc-tab sscc-tab-${item.key} ${
            item.sourcingStrategyCycleId === sourcingStrategyCycleTab
              ? "active"
              : ""
          }`}
          onClick={() =>
            setSourcingStrategyCycleTab(item.sourcingStrategyCycleId)
          }
        >
          <div>{item.step}</div>
          <div>{item.label}</div>
        </div>
      ))}
    </div>
  );
};

const CustomTooltip = ({ title, description, placement, children }) => (
  <Popconfirm
    title={<div className="sscc-popconfirm-title">{title}</div>}
    description={
      <div className="sscc-popconfirm-description">{description}</div>
    }
    trigger="hover"
    showCancel={false}
    okButtonProps={{
      style: {
        display: "none",
      },
    }}
    icon={null}
    placement={placement}
  >
    {children}
  </Popconfirm>
);

const ProcurementLibrary = () => {
  const { isMobile } = useWindowDimensions();
  const [sourcingStrategyCycleTab, setSourcingStrategyCycleTab] = useState(
    SOURCING_STRATEGY_CYCLE_TABS[0]?.key || 1
  );
  const categoryWithAttributes = PLState.useState(
    (s) => s.categoryWithAttributes
  );
  const [practiceInterventions, setPracticeInterventions] = useState([]);

  const sourcingStragegyCycleOptions = useMemo(
    () =>
      categoryWithAttributes
        .find(
          (attr) =>
            attr.id === PROCUREMENT_CATEGORIES_ID.sourcing_strategy_cycle
        )
        ?.attributes?.map((it) => ({ label: it.label, value: it.id })),
    [categoryWithAttributes]
  );

  const remappedSourcingStrategyCycleTabs = useMemo(() => {
    if (!sourcingStragegyCycleOptions?.length) {
      return [];
    }
    const res = sourcingStragegyCycleOptions?.map((it, itx) => {
      const tabContent = SOURCING_STRATEGY_CYCLE_TABS[itx];
      return {
        ...tabContent,
        sourcingStrategyCycleId: it.value,
      };
    });
    setSourcingStrategyCycleTab(res?.[0]?.sourcingStrategyCycleId);
    return res;
  }, [sourcingStragegyCycleOptions]);

  useEffect(() => {
    if (!sourcingStrategyCycleTab) {
      return;
    }
    const url = `/plv2/practices-by-attribute-ids?attribute_ids=${sourcingStrategyCycleTab}&limit=${100}`;
    api
      .get(url)
      .then((res) => {
        setPracticeInterventions(res.data);
      })
      .catch((e) => {
        console.error("Error fetching practice in PL landing page", e);
      });
  }, [sourcingStrategyCycleTab]);

  const handleClick = (id) => {
    // e.preventDefault();

    const link = document.createElement("a");
    link.href = `/procurement-library/intervention-library/${id}`;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    // Simulate Ctrl+Click (Cmd+Click on Mac)
    const clickEvent = new MouseEvent("click", {
      ctrlKey: true,
      metaKey: true, // For Mac
      bubbles: true,
      cancelable: true,
    });

    document.body.appendChild(link);
    link.dispatchEvent(clickEvent);
    document.body.removeChild(link);
  };

  const selectedSourcingStrategyCycleTabContent = useMemo(() => {
    if (!sourcingStrategyCycleTab && !practiceInterventions?.length) {
      return null;
    }
    const active = remappedSourcingStrategyCycleTabs.find(
      (item) => item.sourcingStrategyCycleId === sourcingStrategyCycleTab
    );

    const activeStyles =
      SOURCING_STRATEGY_CYCLE_COLORS?.[active?.key - 1] || {};
    const remappedCollapsedItems = active?.content?.collapseItems?.map(
      (it, idx, arr) => {
        // remap practice interventions with ID
        if (it.key === 4) {
          const remapPIChilds = practiceInterventions?.map((pi) => (
            <li
              key={`pix-${pi.id}`}
              style={{
                ...activeStyles,
                cursor: "pointer",
              }}
              onClick={() => handleClick(pi.id)}
              className={`practice-interventions-list-item-${active?.key}`}
            >
              <CheckIconItem
                text={pi.label}
                color={activeStyles?.shadowColor}
              />
            </li>
          ));
          return {
            ...it,
            children: (
              <ul className="practice-interventions-list">{remapPIChilds}</ul>
            ),
            className:
              idx === arr.length - 1 ? "practice-interventions-item" : "",
          };
        }
        return {
          ...it,
        };
      }
    );
    return {
      ...active?.content,
      collapseItems: remappedCollapsedItems,
    };
  }, [
    sourcingStrategyCycleTab,
    remappedSourcingStrategyCycleTabs,
    practiceInterventions,
  ]);

  return (
    <div
      id="procurement-library"
      className={`procurement-library-container ${
        isMobile ? "mobile-screen" : ""
      }`}
    >
      <div className="jumbotron">
        <div className="jumbotron-content">
          <div className="jumbotron-text">
            <h1>
              Integrating Sustainability
              <br />
              Into Procurement
            </h1>
            <Space className="caption" direction="vertical" size={12}>
              <div>
                These pages provide guidance, prompts and a library of
                procurement practices to help integrate sustainability into
                procurement practices. These resources take you through why
                sustainability brings value, how to get started, and how to
                integrate sustainability into your sourcing strategies.
              </div>
              <div>
                Begin with the introduction to understand core concepts, explore
                practices that fit your business context, then dive into the
                complete intervention library.
              </div>
            </Space>
          </div>

          <div className="jumbotron-cards">
            <List
              grid={{
                gutter: 24,
                xs: 1,
                sm: 1,
                md: 1,
                lg: 3,
                xl: 3,
                xxl: 3,
              }}
              dataSource={cards}
              renderItem={(item, ix) => (
                <List.Item>
                  <Card>
                    <div className="card-content">
                      <div>
                        <div className="card-icon">
                          <span className={`icon-front front-${ix}`}>
                            {item.icon}
                          </span>
                          <span className="icon-back">
                            <OvalIcon />
                          </span>
                        </div>
                        <div className="card-title">
                          <h3>{item.title}</h3>
                        </div>
                      </div>
                      <div className="card-cta">
                        <Link to={item.url}>
                          <Button type="primary">
                            <Space align="center" justify="center">
                              <span>{item.cta}</span>
                              <ArrowRight />
                            </Space>
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          </div>
        </div>
      </div>

      {/* Why Sustainable Procurement? */}
      <div className="pl-section-container pl-section-first-row sustainable-procurement-content">
        <h2>Why Sustainable Procurement?</h2>
        <div className="sustainable-description-wrapper">
          {/* LEFT */}
          <div className="sustainable-description-left">
            <p>
              As businesses face rising costs, tighter regulation and supply
              chain uncertainty - alongside customer demands for transparency
              and lower prices - the need for smarter, more sustainable
              purchasing decisions is stronger than ever.
            </p>
            <p>
              Procurement teams are uniquely placed to take a full value chain
              view of risk, and - working hand-in-hand with sustainability
              colleagues - are fundamental to driving sustainable growth and
              long-term value for people and planet.
            </p>
            <p>
              At its core, procurement exists to align with business strategy,
              manage costs, secure supply and navigate risks that are becoming
              increasingly unpredictable and visible. But procurement strategies
              are only as robust as the business strategies behind them.
            </p>
          </div>
          {/* RIGHT */}
          <div className="sustainable-description-right">
            <p>
              If a company prioritises buying cheaply above all else - ignoring
              human rights and environmental impacts - it inevitably heightens
              value chain risk, from supply disruption and reputational damage
              to customer delisting.
            </p>
            <p>
              The good news is that procurement holds the key to mitigating
              these risks. Through their commercial relationships and influence
              with suppliers, teams can embed sustainable practices - such as
              supplier capacity building - that ripple positively up the value
              chain, creating shared value by shouldering risks together.
            </p>
            <p>
              Not every team is there yet. Embracing sustainability means moving
              beyond a transactional approach towards deeper, more strategic
              supplier partnerships. That mindset shift - towards maturity,
              collaboration and long-term thinking - is what helps unlock the
              real opportunities of sustainable procurement.
            </p>
          </div>
        </div>
        <div className="sustainable-procurement-image">
          <h4>Procurement maturity with suppliers</h4>
          <img
            src={SustainableProcurementImage}
            alt="sustainable-procurement-image"
            className="sp-image"
          />
        </div>
        <Divider />
      </div>
      {/* EOL Why Sustainable Procurement? */}

      {/* Sourcing Strategy Cycle */}
      <section
        id="sourcing-strategy-cycle-content"
        className="pl-section-container sourcing-strategy-cycle-content"
      >
        {/* header */}
        <div className="sscc-header-wrapper">
          <div className="sscc-title-description">
            <h2>Sourcing Strategy Cycle</h2>
            <p>
              At IDH, we support teams at every stage of their sustainable
              procurement journey. We have developed an overview for procurement
              teams on how to think about integrating sustainable practices into
              each step of the four stages of a typical Sourcing Strategy Cycle
              approach - whether a strategy is being created or updated.
            </p>
          </div>
          <div className="sscc-title-button">
            <Link
              to="/procurement-library/intervention-library"
              className="button button-green-fill"
            >
              Explore Intervention Library{" "}
              <ArrowRightOutlined style={{ fontSize: 12, fontWeight: 900 }} />
            </Link>
          </div>
        </div>
        {/* body */}
        <div className="sscc-body-wrapper">
          <div className="sscc-body-left">
            <p>
              Explore each stage of the Cycle and the outline opportunities for
              integrating more sustainable practices.
            </p>

            {/* tabs */}
            <SourcingStrategyCycleTabs
              sourcingStrategyCycleTab={sourcingStrategyCycleTab}
              setSourcingStrategyCycleTab={setSourcingStrategyCycleTab}
              remappedSourcingStrategyCycleTabs={
                remappedSourcingStrategyCycleTabs
              }
            />
            {/* eol tabs */}

            {/* tabs content */}
            {selectedSourcingStrategyCycleTabContent ? (
              <div className="sscc-tabs-content">
                <Space>
                  <img
                    src={selectedSourcingStrategyCycleTabContent.icon}
                    alt="lup-icon.svg"
                    className="icons"
                  />
                  <h3>{selectedSourcingStrategyCycleTabContent.title}</h3>
                </Space>
                <p>{selectedSourcingStrategyCycleTabContent.description}</p>

                <Collapse
                  accordion
                  ghost
                  defaultActiveKey={[1]}
                  expandIconPosition="end"
                  items={selectedSourcingStrategyCycleTabContent.collapseItems}
                  className="sscc-collapse"
                />
              </div>
            ) : (
              ""
            )}
            {/* eol tabs content */}
          </div>
          <div className="sscc-body-right">
            <div>
              <p className="hint-text">Hover over steps/stages for details</p>
            </div>
            <div className="sscc-img-wrapper">
              <img src={SourcingStrategyCycleImage} className="sscc-image" />
              {/* image tooltip helper */}
              {SOURCING_STRATEGY_CYCLE_TOOLTIPS.map((item) => (
                <CustomTooltip key={item.className} {...item}>
                  <div className={`img-tooltip-helper ${item.className}`}>
                    &nbsp;
                  </div>
                </CustomTooltip>
              ))}
            </div>
            {/* eol image tooltip helper */}
          </div>
        </div>
      </section>
      {/* EOL Sourcing Strategy Cycle */}

      {/* Sustainable Procurement Principles */}
      <div className="pl-section-container sustainable-procurement-principles-content">
        <h2>Sustainable Procurement Principles</h2>
        <div className="spp-description-wrapper">
          <div className="sppd-left">
            <p>
              Sustainable procurement practices help enable living wages for
              workers and living incomes for farmers. By adopting sustainable
              procurement, businesses can move away from saddling one actor with
              all the risks to create a model of shared responsibility and
              value.
            </p>
          </div>
          <div className="sppd-right">
            <p>
              IDH&apos;s expert teams can help businesses to implement
              sustainable procurement practices that balance commercial goals
              with fairer purchasing agreements, working to improve both the
              livelihoods and resilience of supply chain farmers and workers. We
              recommend our five core principles are used as a cross check
              before finalising Strategic Choices, in a sourcing strategy cycle.
              Explore them in the video below.
            </p>
          </div>
        </div>
        <div className="spp-image-wrapper">
          <div className="spp-image-border">
            <img
              src={ProcurementPrinciplesCurves}
              alt="procurement-principles-curves-img"
              className="spp-image"
            />
          </div>
        </div>
      </div>
      {/* EOL Sustainable Procurement Principles */}

      {/* Total Cost of Ownership */}
      <div className="pl-section-container total-cost-ownership-content">
        <h2>Total Cost of Ownership</h2>
        <div className="tco-description-wrapper">
          <div className="tcod-left">
            <p>
              The IDH sustainable procurement approach factors in ESG and
              sustainability risks by applying a Total Cost of Ownership (TCO)
              lens across the strategic sourcing cycle. It enables procurement
              and sustainability teams to see a full picture of costs -
              including risks and losses incurred before, during and after
              purchase, while also uncovering new opportunities for value
              creation.
            </p>
            <p>
              TCO ensures smarter choices or trade-offs by moving beyond a sole
              focus on short-term price. Without a TCO approach, purchasing
              strategic materials as cheap commodities creates major risks for a
              business:
            </p>
            <div>
              {TOTAL_COST_OF_OWNERSHIP_CHART_TEXT_CONTENT.filter(
                (_, idx) => idx < 2
              ).map((item, idx) => (
                <p key={`tco-item-${idx}`}>
                  <Space align="top">
                    <img
                      src={CheckCircleIcon}
                      alt="check-circle-icon"
                      className="check-circle-icon"
                    />
                    {item}
                  </Space>
                </p>
              ))}
            </div>
          </div>
          <div className="tcod-right">
            {/* Charts */}
            <img
              src={TCOChartImage}
              alt="total-cost-of-ownership-chart"
              className="tcod-chart"
            />
            <p>{TOTAL_COST_OF_OWNERSHIP_CHART_TEXT_CONTENT[2]}</p>
          </div>
        </div>
      </div>
      {/* EOL Total Cost of Ownership */}

      {/* Other tools */}
      <OtherToolsAndResources
        toolsOrderForLandingPage={["IDC", "IMG", "CII"]}
      />
      {/* EOL Other tools */}
      <FooterDisclaimer disclaimerText="procurement" />
    </div>
  );
};

export default ProcurementLibrary;
